import { useEffect, useRef, useState } from 'react';
import styles from './PrintableArtwork.module.scss';
import Button2 from '../../Buttons/Button2/Button2';
import fonts from '../../../assets/fonts.module.scss';
import { Appearance, loadStripe, StripeCheckoutLoadActionsSuccess, StripeCheckoutSession, StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';

// Mirrors the site's own inputs and labels
const APPEARANCE: Appearance = {
  theme: 'stripe',
  labels: 'above',
  variables: {
    borderRadius: '0px',
    colorBackground: '#ffffff',
    colorDanger: '#b00020',
    colorPrimary: '#000000',
    colorText: '#000000',
    fontFamily: "'Raleway', sans-serif",
    fontSizeBase: '16px',
    spacingUnit: '4px',
  },
  rules: {
    '.Label': {fontSize: '14px', fontWeight: '200', letterSpacing: '1px'},
    '.Input': {border: '1px solid #000000', boxShadow: '2px 2px #f0f0f0', padding: '13px 16px', fontWeight: '200', letterSpacing: '1px'},
    '.Input:focus': {border: '1px solid #000000', boxShadow: '2px 2px #f0f0f0'},
    '.Input--invalid': {border: '1px solid #b00020', boxShadow: '2px 2px #f6d5db'},
    '.Tab': {border: '1px solid #000000', boxShadow: 'none'},
    '.Tab--selected': {border: '1px solid #000000', boxShadow: '2px 2px #f0f0f0'},
  },
};

// Sass exports the URL with its quotes, which PostCSS needs around the // in it
const FONTS = [{cssSrc: fonts.url.replace(/"/g, '')}];

const UNAVAILABLE = 'Payment is unavailable right now; please try again later';
const INCOMPLETE = 'Please complete the highlighted fields';
const SECTION_TITLE_ROOM = 40;

type Totals = {print: string, shipping: string, total: string};

type SectionChange = {complete: boolean, value?: {name?: string, address?: {line1?: string}}};
type SectionState = {complete: boolean, onlyLowerFieldsMissing: boolean};

// The whole checkout for a Checkout Session created by the CreateCheckoutSession lambda: wallet
// buttons, then email, shipping address and payment in Stripe's elements, with the totals, in the
// currency the customer pays in, and the Pay button as our own
const CheckoutForm = ({clientSecret, onPaid, onBack} : {clientSecret: string, onPaid: () => void, onBack: () => void}) => {
  const expressContainer = useRef<HTMLDivElement>(null);
  const contactContainer = useRef<HTMLDivElement>(null);
  const shippingContainer = useRef<HTMLDivElement>(null);
  const billingContainer = useRef<HTMLDivElement>(null);
  const paymentContainer = useRef<HTMLDivElement>(null);
  const currencyContainer = useRef<HTMLDivElement>(null);
  const actions = useRef<StripeCheckoutLoadActionsSuccess | null>(null);
  const [hasWallets, setHasWallets] = useState(false);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Kept while the message fades out, after the error itself is cleared
  const shownError = useRef(error);
  if (error) shownError.current = error;
  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;
  const isMounted = useRef(true);
  // From each section's change events, in page order: the session's addresses aren't kept current as the customer types
  const sections = useRef(new Map<HTMLDivElement, SectionState>());
  useEffect(() => () => { isMounted.current = false; }, []);

  const confirm = async (options: {expressCheckoutConfirmEvent?: StripeExpressCheckoutElementConfirmEvent}) => {
    const checkoutActions = actions.current;
    if (!checkoutActions) return;
    if (!options.expressCheckoutConfirmEvent && !checkoutActions.getSession().canConfirm) {
      // Stripe marks the incomplete fields, but its promise never settles while any are left, so it isn't awaited
      void checkoutActions.validateElements();
      setError(INCOMPLETE);
      const [section, state] = Array.from(sections.current).find(([, {complete}]) => !complete) ?? [];
      // Replaces Stripe's scroll (to the card, or none) with one to the first section left to fill, or to the end of
      // a tall address missing only its last fields. Only the column moves; scrollIntoView would move the page too
      setTimeout(() => {
        const column = section?.parentElement;
        if (!section || !state || !column) return;
        const sectionBox = section.getBoundingClientRect(), columnBox = column.getBoundingClientRect();
        const offset = state.onlyLowerFieldsMissing
          ? Math.max(sectionBox.bottom - columnBox.bottom, sectionBox.top - columnBox.top - SECTION_TITLE_ROOM)
          : sectionBox.top - columnBox.top - SECTION_TITLE_ROOM;
        column.scrollTo({top: column.scrollTop + offset, behavior: 'smooth'});
      }, 300);
      return;
    }
    setError(null);
    setIsPaying(true);
    try {
      // Redirecting to return_url would reload the site, so only payment methods that need it do
      const result = await checkoutActions.confirm({...options, redirect: 'if_required'});
      if (result.type === 'success') {
        onPaidRef.current();
        return;
      }
      if (isMounted.current) setError(result.error.message);
    } catch (confirmError) {
      console.error('Payment confirmation error:', confirmError);
      if (isMounted.current) setError('The payment could not be completed; please try again');
    }
    if (isMounted.current) setIsPaying(false);
  };
  const confirmRef = useRef(confirm);
  confirmRef.current = confirm;

  useEffect(() => {
    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    const containers = [expressContainer, contactContainer, shippingContainer, billingContainer, paymentContainer, currencyContainer].map(ref => ref.current);
    if (!publishableKey || containers.some(container => !container)) {
      setError(UNAVAILABLE);
      return;
    }
    const [express, contact, shipping, billing, payment, currency] = containers as HTMLDivElement[];
    let isActive = true;
    let mountedElements: {unmount: () => void}[] = [];

    const showSession = (session: StripeCheckoutSession) => {
      if (!isActive) return;
      const {subtotal, shippingRate, total} = session.total;
      setTotals({print: subtotal.amount, shipping: shippingRate.minorUnitsAmount === 0 ? 'Free' : shippingRate.amount, total: total.amount});
      if (session.canConfirm) setError(current => current === INCOMPLETE ? null : current);
    };

    const start = async () => {
      // The site is in English; Stripe would otherwise follow the browser's language
      const stripe = await loadStripe(publishableKey, {locale: 'en'});
      if (!isActive) return;
      if (!stripe) throw new Error('Stripe.js is unavailable');
      const checkout = stripe.initCheckoutElementsSdk({
        clientSecret,
        elementsOptions: {appearance: APPEARANCE, fonts: FONTS},
        adaptivePricing: {allowed: true},
      });

      const expressElement = checkout.createExpressCheckoutElement();
      expressElement.on('ready', ({availablePaymentMethods}) => setHasWallets(!!availablePaymentMethods));
      expressElement.on('confirm', (event) => confirmRef.current({expressCheckoutConfirmEvent: event}));
      const elements = [
        [expressElement, express],
        [checkout.createContactDetailsElement(), contact],
        [checkout.createShippingAddressElement(), shipping],
        // With both address elements, Stripe shows a "same as shipping" checkbox on the billing one
        [checkout.createBillingAddressElement(), billing],
        [checkout.createPaymentElement(), payment],
        // Shows nothing unless the customer can pay in their own currency
        [checkout.createCurrencySelectorElement(), currency],
      ] as const;
      elements.forEach(([element, container]) => element.mount(container));
      sections.current.clear();
      // The sections a customer fills in: neither the wallet buttons nor the currency selector
      elements.slice(1, -1).forEach(([element, container]) => {
        sections.current.set(container, {complete: false, onlyLowerFieldsMissing: false});
        (element as {on: (event: 'change', handler: (change: SectionChange) => void) => void}).on('change', (change) => {
          sections.current.set(container, {complete: change.complete, onlyLowerFieldsMissing: !!(change.value?.name && change.value.address?.line1)});
        });
      });
      mountedElements = elements.map(([element]) => element);

      checkout.on('change', showSession);
      const loaded = await checkout.loadActions();
      if (!isActive) return;
      if (loaded.type !== 'success') throw loaded.error;
      actions.current = loaded.actions;
      setIsReady(true);
      showSession(loaded.actions.getSession());
    };
    start().catch((loadError) => {
      if (!isActive) return;
      console.error('Failed to load the checkout:', loadError);
      setError(UNAVAILABLE);
    });

    return () => {
      isActive = false;
      actions.current = null;
      mountedElements.forEach(element => element.unmount());
    };
  }, [clientSecret]);

  return (<>
    <div ref={expressContainer} className={styles.StripeElement} data-testid="ExpressCheckout"/>
    {hasWallets && <p className={styles.Divider}>or</p>}
    <div ref={contactContainer} className={styles.StripeElement} data-testid="ContactDetails"/>
    <h3 className={styles.SectionTitle}>Shipping details</h3>
    <div ref={shippingContainer} className={styles.StripeElement} data-testid="ShippingAddress"/>
    <h3 className={styles.SectionTitle}>Billing details</h3>
    <div ref={billingContainer} className={styles.StripeElement} data-testid="BillingAddress"/>
    <div ref={paymentContainer} className={styles.StripeElement} data-testid="PaymentForm"/>
    <div ref={currencyContainer} className={styles.StripeElement}/>
    {totals && (
      <dl className={styles.Summary}>
        <div className={styles.SummaryLine}><dt>Print</dt><dd>{totals.print}</dd></div>
        <div className={styles.SummaryLine}><dt>Shipping</dt><dd>{totals.shipping}</dd></div>
        <div className={`${styles.SummaryLine} ${styles.Total}`}><dt>Total</dt><dd>{totals.total}</dd></div>
      </dl>
    )}
    <p className={styles.Notice}>Prints ship from the United States: outside it, import duties and taxes may be charged on delivery.</p>
    <div className={`${styles.Message} ${error ? styles.MessageShown : ''}`} aria-hidden={!error} data-testid="CheckoutMessage">
      <p className={styles.Error}>{shownError.current}</p>
    </div>
    <div className={styles.Buttons}>
      <Button2 action={onBack} disabled={isPaying}>Previous</Button2>
      <Button2 action={() => confirm({})} disabled={!isReady || isPaying}>{isPaying ? 'Paying…' : 'Pay'}</Button2>
    </div>
  </>);
};

export default CheckoutForm;
